import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, BarChart3, CreditCard, Puzzle, MessageSquare,
  Settings, LogOut, Menu, Star, MessageCircle, Share2, Award, Users,
  Zap, Sparkles, RefreshCw, Library, Store, ScanEye, Wand2, 
  Target, Radar, BrainCircuit, Key
} from 'lucide-react';
import { LiveChatWidget } from '../components/LiveChatWidget';

const navItems = [
  { name: 'Overview', href: '/dashboard', icon: LayoutDashboard },
  { name: 'PI Check', href: '/dashboard/check', icon: ScanEye },
  { name: 'PI Craft', href: '/dashboard/craft', icon: Wand2 },
  { name: 'PI Cast', href: '/dashboard/cast', icon: Target },
  { name: 'PI Loop', href: '/dashboard/loop', icon: Radar },
  { name: 'Token Optimizer', href: '/dashboard/optimizer', icon: Zap },
  { name: 'My Casts', href: '/dashboard/my-casts', icon: Library },
  { name: 'PI Store', href: '/dashboard/store', icon: Store },
  { name: 'Usage', href: '/dashboard/usage', icon: BarChart3 },
  { name: 'Progress Report', href: '/dashboard/progress', icon: BrainCircuit },
  { name: 'Achievements', href: '/dashboard/achievements', icon: Award },
  { name: 'Refer & Earn', href: '/dashboard/referrals', icon: Users },
  { name: 'Extension', href: '/dashboard/extension', icon: Puzzle },
  // { name: 'MCP & API', href: '/dashboard/mcp', icon: Key }, // Hidden for pilot — re-enable when ready
  { name: 'Testimonial', href: '/dashboard/testimonial', icon: Star },
  { name: 'Support', href: '/dashboard/support', icon: MessageSquare },
  { name: 'Settings', href: '/dashboard/settings', icon: Settings },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isActive = (href) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-[#060C18]" data-testid="dashboard-layout">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-[#0A1222] border-r border-white/5 z-50 transform transition-transform lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/5">
            <Link to="/" className="flex items-center gap-2">
              <img src="/sendpi-icon.png" alt="SendPI" className="h-8 w-auto" />
              <span className="font-space text-xl font-bold text-white">Send<span className="text-[var(--pi-cyan)]">PI</span></span>
            </Link>
          </div>

          {/* User info */}
          <div className="p-4 border-b border-white/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center text-white font-medium">
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">{user?.name}</p>
                <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-cyan-400 border border-cyan-500/30">
                  {user?.plan?.toUpperCase() || 'FREE'}
                </span>
              </div>
            </div>
          </div>

          {/* Navigation — scrollable */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto scrollbar-thin" data-testid="sidebar-nav">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                  isActive(item.href)
                    ? 'bg-white/5 text-white'
                    : 'text-white/55 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="w-4 h-4 shrink-0" />
                <span className="truncate">{item.name}</span>
              </Link>
            ))}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-white/5">
            <button
              onClick={logout}
              className="flex items-center gap-3 px-4 py-3 w-full rounded-xl text-white/55 hover:text-red-400 hover:bg-white/5 transition-colors"
              data-testid="sidebar-logout"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-[#060C18]/80 backdrop-blur-xl border-b border-white/5">
          <div className="flex items-center justify-between px-6 py-4">
            <button
              className="lg:hidden p-2 text-white/70 hover:text-white"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex items-center gap-4 ml-auto">
              {/* Credits display */}
              <div className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
                <div className="w-2 h-2 rounded-full bg-cyan-400" />
                <span className="text-white/70 text-sm">
                  <span className="text-white font-medium">{user?.credits_remaining || 0}</span> credits
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6 md:p-8">
          <Outlet />
        </main>
        <LiveChatWidget />
      </div>
    </div>
  );
}
